import { SUPPORTED_FILE_EXTENSIONS } from '@/backend/types'
import { useEntityStore } from '@/data/useEntityStore'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import { useFieldUpload } from '@/modules/Project/useFieldUpload'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { toast } from '@/shared/toast'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import { toValue, type MaybeRef } from 'vue'

type UploadToFileFieldParams = {
  workspaceId: string
  projectId: string
  entityId: string | undefined
  propertyId: string
  parentEntityId: string | null
  filesToUpload: File[]
}

/**
 * Provides a function that can be used to upload files to fields for a given
 * file property.
 *
 * If given an entity ID it will upload the first file to the given entity and
 * add the rest to the pending uploads.
 *
 * If not given an entity ID it will create a new entity, upload the first file
 * to it and add the rest to the pending uploads.
 *
 * @param supportedMimeTypes - The mime types that are supported by the file field. If
 * undefined then we assume that all MIME types are supported.
 */
export const useUploadToFileField = (supportedMimeTypes: MaybeRef<string[] | undefined>) => {
  const uploadStore = useFieldUpload()
  const { workspaceId } = useWorkspaceContext()
  const supportedFileExtensions = [...SUPPORTED_FILE_EXTENSIONS]
  const projectStore = useProjectStore()
  const entityStore = useEntityStore()
  const { captureAnalyticsEvent } = useAnalytics()
  const { addEntity } = useLimitedAction()

  return async ({
    filesToUpload,
    entityId: _entityId,
    parentEntityId,
    propertyId,
    projectId,
  }: UploadToFileFieldParams) => {
    /**
     * This should never really happen - something very weird is happening if a user
     * is uploading a file without being in a workspace or project.
     */
    invariant(workspaceId.value, 'No workspace ID found when uploading a file.')

    let entityId = _entityId
    if (!entityId) {
      const res = await addEntity({
        workspaceId: workspaceId.value,
        projectId,
        parentEntityId,
      })
      if (!res.ok) {
        throw new Error('Failed to create entity')
      }

      const serializedEntity = entityStore.pushEntityJSON(res.data)
      projectStore.upsertEntity(serializedEntity)

      entityId = res.data.id
    }

    const files = Array.from(filesToUpload)
    const supportedTypes = toValue(supportedMimeTypes)
    // Some files have no type, so we fallback to the extension
    // https://developer.mozilla.org/en-US/docs/Web/API/Blob/type
    const unsupportedFiles = supportedTypes
      ? files.filter((f) =>
          f.type === ''
            ? !(f.name.split('.').pop() ?? '' in supportedTypes)
            : !supportedTypes.includes(f.type),
        )
      : [] // undefined means all mime types are supported so no files are unsupported

    if (unsupportedFiles.length > 0) {
      toast.warning(
        [
          'Some of the files you tried to upload are not supported, so the upload was skipped.',
          'We only support the following file extensions:',
          supportedFileExtensions.join(', ').concat('.'),
        ].join(' '),
      )
      return
    }

    const [file, ...moreFiles] = files

    if (file) {
      uploadStore.addUpload(
        {
          workspaceId: workspaceId.value,
          projectId,
          entityId,
          propertyId,
          parentEntityId: parentEntityId || null,
        },
        file,
      )
    }

    if (moreFiles.length === 0) {
      return
    }

    const currentIndex = projectStore.activeView?.entityIdToIndex?.get(entityId) ?? -1
    const remainingEntities = projectStore.activeView?.entities?.slice(currentIndex + 1) ?? []

    let foundCount = 0
    const pendingUpdates: {
      workspaceId: string
      projectId: string
      entityId: string
      propertyId: string
      parentEntityId: string | null
      file: File
    }[] = []
    for (let i = 0; foundCount < moreFiles.length && i <= remainingEntities.length; i++) {
      const entity = remainingEntities[i]
      const file = nonNullable(moreFiles[foundCount])
      if (entity && !entity.fields.get(propertyId)?.manualValue) {
        pendingUpdates.push({
          workspaceId: workspaceId.value,
          projectId,
          entityId: entity.id,
          propertyId,
          file,
          parentEntityId: parentEntityId || null,
        })
        foundCount++
      }
    }

    pendingUpdates.forEach((u) => {
      uploadStore.addUpload(
        {
          workspaceId: u.workspaceId,
          projectId: u.projectId,
          entityId: u.entityId,
          propertyId: u.propertyId,
          parentEntityId: u.parentEntityId,
        },
        u.file,
      )

      captureAnalyticsEvent(ANALYTICS_EVENT.FILE_UPLOAD_PENDING, {
        workspaceId: u.workspaceId,
        projectId: u.projectId,
        entityId: u.entityId,
        propertyId: u.propertyId,
      })
    })

    const pendingCreates = moreFiles.slice(pendingUpdates.length)
    if (pendingCreates.length > 0) {
      uploadStore.addPendingUploads(
        {
          workspaceId: workspaceId.value,
          projectId,
          propertyId,
          parentEntityId: parentEntityId || null,
        },
        pendingCreates,
      )
    }
  }
}
